/**
 * Helpers für die Voiceover-Spur. Unsere "echte" Repräsentation ist eine
 * Liste von `VoiceoverSegment`s (mehrere Schnitt-Stücke einer Aufnahme,
 * frei auf der Timeline platziert). Aus Kompatibilitätsgründen mit alten
 * Reels (vor dem Multi-Segment-Refactor) gibts ausserdem einen Legacy-
 * Single-Clip-Pfad mit `fromSec/durSec/trimStartSec/...` direkt auf der
 * Config.
 *
 * Diese Util normalisiert / migriert solche alten Configs in die neue
 * Form (segments[] mit mindestens einem Eintrag) und stellt CRUD-Helfer
 * bereit, die der Composer und das VoiceoverPanel benutzen können.
 */
import type { VoiceoverConfig, VoiceoverSegment } from "../api";

let __segUid = 0;
export function newSegmentId(): string {
  __segUid += 1;
  return `seg_${Date.now().toString(36)}_${__segUid.toString(36)}`;
}

/**
 * Stellt sicher, dass `voiceover.segments` existiert + mindestens ein Segment
 * enthält. Falls die Config nur Legacy-Felder hat, wird daraus ein einzelnes
 * Segment gebaut. Gibt eine NEUE Config zurück (nie die übergebene Referenz)
 * — sicher als Setter-Argument verwendbar.
 */
export function ensureSegments(vo: VoiceoverConfig): VoiceoverConfig {
  if (Array.isArray(vo.segments) && vo.segments.length > 0) {
    return vo;
  }
  const seg: VoiceoverSegment = {
    id: newSegmentId(),
    fromSec: vo.fromSec ?? 0,
    durSec: vo.durSec ?? 5,
    trimStartSec: vo.trimStartSec ?? 0,
    fadeInSec: vo.fadeInSec ?? 0,
    fadeOutSec: vo.fadeOutSec ?? 0,
  };
  return {
    ...vo,
    segments: [seg],
    // Legacy-Felder gleich auf den Segment-Wert syncen, damit ältere
    // Reader (oder die Single-Clip-Engine-Fallback-Pfade) ohne Knack
    // weiterlesen können.
    fromSec: seg.fromSec,
    durSec: seg.durSec,
    trimStartSec: seg.trimStartSec,
    fadeInSec: seg.fadeInSec,
    fadeOutSec: seg.fadeOutSec,
  };
}

/** Liefert eine sortierte Kopie der Segmente (kürzester fromSec zuerst). */
export function getSortedSegments(vo: VoiceoverConfig | undefined): VoiceoverSegment[] {
  if (!vo?.segments?.length) return [];
  return [...vo.segments].sort((a, b) => a.fromSec - b.fromSec);
}

/** Aktualisiert ein einzelnes Segment per id. Gibt eine NEUE Config zurück. */
export function patchSegment(
  vo: VoiceoverConfig,
  id: string,
  patch: Partial<Omit<VoiceoverSegment, "id">>,
): VoiceoverConfig {
  const segs = vo.segments ?? [];
  const next = segs.map((s) => (s.id === id ? { ...s, ...patch } : s));
  return { ...vo, segments: next, ...syncLegacyFromFirst(next) };
}

/** Entfernt ein Segment per id. Wenn das letzte entfernt wird, geben wir
 *  die Config trotzdem mit leerem segments[] zurück — der Caller (Composer)
 *  entscheidet, ob die ganze VO-Spur gelöscht werden soll. */
export function removeSegment(vo: VoiceoverConfig, id: string): VoiceoverConfig {
  const next = (vo.segments ?? []).filter((s) => s.id !== id);
  return { ...vo, segments: next, ...syncLegacyFromFirst(next) };
}

/** Fügt ein neues Segment hinzu. */
export function addSegment(
  vo: VoiceoverConfig,
  seg: Omit<VoiceoverSegment, "id"> & { id?: string },
): VoiceoverConfig {
  const newSeg: VoiceoverSegment = { id: seg.id ?? newSegmentId(), ...seg };
  const next = [...(vo.segments ?? []), newSeg];
  return { ...vo, segments: next, ...syncLegacyFromFirst(next) };
}

/**
 * Schneidet das Segment, das den Playhead enthält, in zwei Stücke:
 * – Linkes Stück: behält `fromSec` und `trimStartSec`, neue `durSec` = (playhead − fromSec).
 * – Rechtes Stück: `fromSec = playhead`, `durSec = original − leftDur`,
 *   `trimStartSec = original.trimStartSec + leftDur`.
 * Gibt die neue Config zurück + die ID des neu entstandenen rechten Stücks
 * (für Auto-Selektion).
 */
export function splitAtPlayhead(
  vo: VoiceoverConfig,
  playheadSec: number,
): { config: VoiceoverConfig; newSegmentId: string | null } {
  const segs = vo.segments ?? [];
  // Finde das Segment, das den Playhead enthält. Mit kleinem Toleranz-Epsilon,
  // damit ein Klick exakt auf einer Segment-Kante kein Splitting auslöst.
  const eps = 0.05;
  const target = segs.find(
    (s) => playheadSec > s.fromSec + eps && playheadSec < s.fromSec + s.durSec - eps,
  );
  if (!target) return { config: vo, newSegmentId: null };
  const leftDur = playheadSec - target.fromSec;
  const rightDur = target.durSec - leftDur;
  const rightId = newSegmentId();
  const left: VoiceoverSegment = {
    ...target,
    durSec: leftDur,
    fadeOutSec: 0, // saubere Schnittkante — kein Fade-Out auf der linken Hälfte.
  };
  const right: VoiceoverSegment = {
    id: rightId,
    fromSec: playheadSec,
    durSec: rightDur,
    trimStartSec: target.trimStartSec + leftDur,
    fadeInSec: 0, // saubere Schnittkante — kein Fade-In auf der rechten Hälfte.
    fadeOutSec: target.fadeOutSec ?? 0,
  };
  const next = segs
    .map((s) => (s.id === target.id ? left : s))
    .concat(right);
  return {
    config: { ...vo, segments: next, ...syncLegacyFromFirst(next) },
    newSegmentId: rightId,
  };
}

/** Liefert die Legacy-Felder (fromSec/durSec/...) aus dem ersten Segment,
 *  damit JSON-Reader, die nur die alten Felder kennen, immer noch eine
 *  korrekte Vorschau bauen können. */
function syncLegacyFromFirst(segs: VoiceoverSegment[]): Pick<
  VoiceoverConfig,
  "fromSec" | "durSec" | "trimStartSec" | "fadeInSec" | "fadeOutSec"
> {
  if (segs.length === 0) {
    return {
      fromSec: 0,
      durSec: 0,
      trimStartSec: 0,
      fadeInSec: 0,
      fadeOutSec: 0,
    };
  }
  const sorted = [...segs].sort((a, b) => a.fromSec - b.fromSec);
  const first = sorted[0];
  return {
    fromSec: first.fromSec,
    durSec: first.durSec,
    trimStartSec: first.trimStartSec,
    fadeInSec: first.fadeInSec ?? 0,
    fadeOutSec: first.fadeOutSec ?? 0,
  };
}
